/**
 * WeeklyDigestCard Component
 *
 * Weekly digest settings card with:
 * - Day and time configuration
 * - Content section toggles (performance, health, attention, trends)
 * - Preview digest button
 *
 * Note: Weekly digest is always enabled - filtering is done through recipients.
 * Users can enable/disable digest delivery per-recipient in RecipientListSection.
 *
 * Design Compliance:
 * - Single Card pattern (matches other settings cards)
 * - Icon + Title header
 * - Uses Polaris spacing scale (gap="400", gap="500")
 */

import { useMemo, useCallback, useState } from 'react';
import { useSearchParams } from 'react-router';
import {
  Card,
  BlockStack,
  InlineStack,
  Text,
  Box,
  Icon,
  Select,
  Button,
  Tooltip,
} from '@shopify/polaris';
import { CalendarIcon, ViewIcon } from '@shopify/polaris-icons';
import { Toggle } from '../../ui';
import { AppBridgeModal } from '../../app-bridge/AppBridgeModal';
import { useModalRoute } from '../../../hooks/core/useModalRoute';

export interface WeeklyDigestCardProps {
  /** Weekly digest day (0=Sunday, 6=Saturday) */
  weeklyDigestDay: 0 | 1 | 2 | 3 | 4 | 5 | 6;
  /** Weekly digest time (HH:MM format) */
  weeklyDigestTime: string;
  /** Handler for weekly digest day change */
  onWeeklyDigestDayChange: (day: number) => void;
  /** Handler for weekly digest time change */
  onWeeklyDigestTimeChange: (time: string) => void;
  /** Weekly digest sections */
  weeklyDigestSections: Array<string>;
  /** Handler for weekly digest sections change */
  onWeeklyDigestSectionsChange: (sections: Array<string>) => void;
  /** Shop domain for test digest feature */
  shopDomain?: string;
}

const DAY_OPTIONS = [
  { label: 'Sunday', value: '0' },
  { label: 'Monday', value: '1' },
  { label: 'Tuesday', value: '2' },
  { label: 'Wednesday', value: '3' },
  { label: 'Thursday', value: '4' },
  { label: 'Friday', value: '5' },
  { label: 'Saturday', value: '6' },
];

/** Inline toggle row for digest section options */
interface ToggleRowProps {
  label: string;
  enabled: boolean;
  onChange: (enabled: boolean) => void;
}

function ToggleRow({ label, enabled, onChange }: ToggleRowProps) {
  return (
    <InlineStack align="space-between" blockAlign="center" wrap={false}>
      <Text as="span" variant="bodyMd">
        {label}
      </Text>
      <Toggle
        label={label}
        enabled={enabled}
        onChange={onChange}
      />
    </InlineStack>
  );
}

export function WeeklyDigestCard({
  weeklyDigestDay,
  weeklyDigestTime,
  onWeeklyDigestDayChange,
  onWeeklyDigestTimeChange,
  weeklyDigestSections,
  onWeeklyDigestSectionsChange,
  shopDomain = '',
}: WeeklyDigestCardProps) {
  const [searchParams] = useSearchParams();

  // Route-based modal for preview digest
  const previewDigestModal = useModalRoute('/app/settings/modal/preview-digest');

  // Sync modal open state with route-based modal
  const handleOpenPreview = useCallback(() => {
    const shop = searchParams.get('shop') || shopDomain;
    previewDigestModal.open({
      shop,
      sections: JSON.stringify(weeklyDigestSections),
    });
  }, [searchParams, shopDomain, weeklyDigestSections, previewDigestModal]);

  const handleClosePreview = useCallback(() => {
    previewDigestModal.close();
  }, [previewDigestModal]);

  // Time options for selects (30-min intervals)
  const timeOptions = useMemo(() => {
    const options: Array<{ label: string; value: string }> = [];
    for (let hour = 0; hour < 24; hour++) {
      for (const minute of [0, 30]) {
        const hourStr = hour.toString().padStart(2, '0');
        const minStr = minute.toString().padStart(2, '0');
        const value = `${hourStr}:${minStr}`;
        const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
        const ampm = hour < 12 ? 'AM' : 'PM';
        const label = `${displayHour}:${minStr.padStart(2, '0')} ${ampm}`;
        options.push({ label, value });
      }
    }
    return options;
  }, []);

  return (
    <>
      <Card>
        <BlockStack gap="400">
          {/* Header */}
          <InlineStack align="space-between" blockAlign="start" wrap={false}>
            <InlineStack gap="200" blockAlign="start" wrap={false}>
              <Box paddingBlockStart="050">
                <Icon source={CalendarIcon} tone="subdued" />
              </Box>
              <BlockStack gap="100">
                <Text as="h2" variant="headingMd">
                  Weekly digest
                </Text>
                <Text as="p" variant="bodySm" tone="subdued">
                  Summary sent to recipients with digest enabled
                </Text>
              </BlockStack>
            </InlineStack>
            {shopDomain && (
              <Tooltip content="Preview digest">
                <Button
                  icon={ViewIcon}
                  variant="plain"
                  onClick={handleOpenPreview}
                  accessibilityLabel="Preview digest"
                />
              </Tooltip>
            )}
          </InlineStack>

          {/* Content */}
          <BlockStack gap="400">
            <InlineStack gap="300" blockAlign="end">
              <Box minWidth="140px">
                <Select
                  label="Day"
                  labelHidden
                  options={DAY_OPTIONS}
                  value={String(weeklyDigestDay)}
                  onChange={(value) => onWeeklyDigestDayChange(Number(value))}
                />
              </Box>
              <Box minWidth="140px">
                <Select
                  label="Time"
                  labelHidden
                  options={timeOptions}
                  value={weeklyDigestTime}
                  onChange={onWeeklyDigestTimeChange}
                />
              </Box>
            </InlineStack>

            <BlockStack gap="300">
              <ToggleRow
                label="Weekly performance summary"
                enabled={weeklyDigestSections.includes('performance_summary')}
                onChange={(enabled) => {
                  const newSections = enabled
                    ? [...weeklyDigestSections, 'performance_summary']
                    : weeklyDigestSections.filter((s) => s !== 'performance_summary');
                  onWeeklyDigestSectionsChange(newSections);
                }}
              />
              <ToggleRow
                label="Inventory health overview"
                enabled={weeklyDigestSections.includes('health_overview') || weeklyDigestSections.includes('alerts')}
                onChange={(enabled) => {
                  // Remove both legacy and new section names
                  let newSections = weeklyDigestSections.filter((s) => s !== 'health_overview' && s !== 'alerts');
                  if (enabled) {
                    newSections = [...newSections, 'health_overview'];
                  }
                  onWeeklyDigestSectionsChange(newSections);
                }}
              />
              <ToggleRow
                label="Products requiring attention"
                enabled={weeklyDigestSections.includes('attention_required') || weeklyDigestSections.includes('top_products')}
                onChange={(enabled) => {
                  // Remove both legacy and new section names
                  let newSections = weeklyDigestSections.filter((s) => s !== 'attention_required' && s !== 'top_products');
                  if (enabled) {
                    newSections = [...newSections, 'attention_required'];
                  }
                  onWeeklyDigestSectionsChange(newSections);
                }}
              />
              <ToggleRow
                label="Velocity trends"
                enabled={weeklyDigestSections.includes('velocity_trends')}
                onChange={(enabled) => {
                  const newSections = enabled
                    ? [...weeklyDigestSections, 'velocity_trends']
                    : weeklyDigestSections.filter((s) => s !== 'velocity_trends');
                  onWeeklyDigestSectionsChange(newSections);
                }}
              />
            </BlockStack>
          </BlockStack>
        </BlockStack>
      </Card>

      {/* Preview Digest Modal - Route-based */}
      {shopDomain && (
        <AppBridgeModal
          src={previewDigestModal.src}
          open={previewDigestModal.isOpen}
          onClose={handleClosePreview}
          variant="large"
        />
      )}
    </>
  );
}
